import {
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  mergeMap,
  take,
  throttleTime,
} from 'rxjs';
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { uniq } from 'lodash';

import {
  useItineraryStore,
  useMetaStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import {
  assertValue,
  performanceMark,
  logged,
  isReisegiganten,
  asObservable,
  isDefined,
} from '@libs/utils';
import type {
  FlightIngredientResponse,
  FerryIngredientResponse,
  PortStaticInfo,
  ItinerarySearchUserCriteria,
  ItineraryWithPrice,
  TransportLeg,
  DestinationSuggestedActivitiesSearchCriteria,
  ItinerarySearchCriteria,
} from '@libs/trip-planner-interfaces';
import { IngredientType, SearchTermType } from '@libs/trip-planner-interfaces';
import {
  isAvailableFlightIngredientResponse,
  isAvailableTransportIngredientResponse,
} from '@libs/search-service-smart-search';
import { trackReisegigantenNoAvailability } from '@libs/google-analytics-events';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
import { flatIatas } from '../recipe/ingredients/helpers';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
} from '../recipe/ingredients/typeGuards';
import type { HotelIngredient } from '../recipe/ingredients';

import {
  shouldClearSingleItinerary,
  shouldSearchCurrentItinerary,
} from './ItineraryViewModel.logic';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useItineraryViewModel(model: TripPlannerModel) {
  const itineraryStore = useItineraryStore();
  const criteriaStore = useCriteriaStore();
  const { selectedItinerary } = storeToRefs(itineraryStore);
  const preferredTimeStore = usePreferredTimeStore();
  const recipeStore = useRecipeStore();
  const metaStore = useMetaStore();
  const tripStore = useTripStore();
  const shouldSkipNextItinerarySearch = ref<boolean>();

  function searchItinerariesFromCriteria(
    criteria: ItinerarySearchCriteria
  ): void {
    if (!criteria.userCriteria.ingredients?.length) {
      tripStore.clearOverviewStaticInfo();
      itineraryStore.clearItineraries();
      return;
    }

    if (!recipeStore.isRecipeCorrect || isFeatureToggleActive('loader')) {
      return;
    }

    if (recipeStore.shouldSkipItinerarySearch) {
      recipeStore.updateShouldSkipItinerarySearch(false);
      return;
    }

    //TODO remove this logic as soon as we stop setting number of nights after being suggested
    if (shouldSkipNextItinerarySearch.value) {
      shouldSkipNextItinerarySearch.value = false;
      return;
    }

    shouldSkipNextItinerarySearch.value = shouldCriteriaSkipNextIteration(
      criteria.userCriteria
    );

    model.searchItineraries(criteria);
  }

  watch(
    () => criteriaStore.itineraryCriteria,
    () => {
      searchItinerariesFromCriteria({
        userCriteria: criteriaStore.itineraryCriteria,
        businessCriteria: criteriaStore.businessCriteria,
      });
    },
    { deep: true }
  );

  watch(
    () => criteriaStore.destinationSuggestedActivitiesCriteria,
    (criteria) => {
      searchDestinationSuggestedActivitiesFromCriteria(criteria);
    },
    { deep: true, immediate: true }
  );

  function searchDestinationSuggestedActivitiesFromCriteria(
    criteria: DestinationSuggestedActivitiesSearchCriteria
  ): void {
    criteria.destinations.forEach(({ constraint, ingredientId }) => {
      const hasOnlyHotelCode =
        !!constraint.hotelCodes?.length &&
        !constraint.polygonId &&
        !constraint.destination;

      if (hasOnlyHotelCode) {
        return;
      }

      model
        .searchDestinationSuggestedActivities(constraint)
        .subscribe((suggestedActivitiesStaticInfo) =>
          itineraryStore.insertDestinationSuggestedActivities(
            suggestedActivitiesStaticInfo,
            ingredientId
          )
        );
    });
  }

  // NOTE: Disable automatic activity suggestions search to check if this piece of code was responsible for
  //       reported incident issues around activity availability search response times
  //       https://semboteam.slack.com/archives/C0852F0THHS/p1734340291771089

  // watch(
  //   criteriaState.activitySuggestionsCriteria,
  //   (criteria) => searchActivitySuggestionsFromCriteria(criteria),
  //   { immediate: true }
  // );

  // function searchActivitySuggestionsFromCriteria(
  //   criterias: ActivitySuggestionsSearchCriteria[]
  // ): void {
  //   criterias.forEach((criteria) => {
  //     model
  //       .getSuggestedActivities(criteria)
  //       .pipe(
  //         mergeMap((suggestions) => {
  //           const codes = suggestions.suggestions.map(
  //             (suggestion) => suggestion.code
  //           );
  //           return model.getActivitiesStaticData(codes).pipe(
  //             map(
  //               (
  //                 staticData: ActivityStaticInfo[]
  //               ): ActivitySuggestionsByIngredientId | null => {
  //                 const ingredientId =
  //                   criteria.userCriteria.recipe.ingredients?.[
  //                     suggestions.ingredientIndex
  //                   ]?.ingredientId;

  //                 if (ingredientId) {
  //                   const activitySuggestionsDictionary: ActivitySuggestionsByIngredientId =
  //                     {
  //                       [ingredientId]: [],
  //                     };

  //                   staticData.forEach((data) => {
  //                     const suggestion = suggestions.suggestions.find(
  //                       (val) => val.code === data.hotelCode
  //                     );
  //                     if (suggestion) {
  //                       activitySuggestionsDictionary[ingredientId]?.push({
  //                         ...suggestion,
  //                         name: data.name,
  //                         images: data.images,
  //                       });
  //                     }
  //                   });

  //                   return activitySuggestionsDictionary;
  //                 }

  //                 return null;
  //               }
  //             )
  //           );
  //         })
  //       )
  //       .subscribe(itineraryStore.insertActivitySuggestions);
  //   });
  // }

  function shouldCriteriaSkipNextIteration(
    criteria: ItinerarySearchUserCriteria
  ): boolean {
    return !!criteria.ingredients?.some(
      (ingredient) =>
        (isHotelIngredientAdapter(ingredient) ||
          isOwnStayIngredientAdapter(ingredient)) &&
        !isDefined(ingredient.numberOfNights)
    );
  }

  const searchedCarrierNames = new Set<string>();

  model.itinerarySearches.subscribe(() => {
    itineraryStore.itinerarySearchCounter++;
    itineraryStore.staticItinerarySearchCounter++;
    itineraryStore.globalSuggestionItinerariesCounter++;

    searchedCarrierNames.clear();

    const recipeCarrierFilterCodes = uniq(
      recipeStore.transportOnlyRecipe.flatMap(
        (transportIngredient) => transportIngredient.filters.carrierFilters
      )
    );

    recipeCarrierFilterCodes.forEach((carrierCode) =>
      searchedCarrierNames.add(carrierCode)
    );

    model
      .searchCarrierNames(recipeCarrierFilterCodes)
      .subscribe((carrierNames) => {
        tripStore.setCarrierNames(carrierNames);
      });
  });

  const firstItineraryReceived = model.itinerarySearchResults.pipe(
    mergeMap((results) => results.itineraries.pipe(take(1)))
  );
  firstItineraryReceived.subscribe(() =>
    performanceMark('FirstItineraryReceived')
  );

  model.itinerarySearchResults.pipe(take(1)).subscribe((result) =>
    result.itineraries.subscribe({
      complete: () => {
        if (
          metaStore.metaAffiliateId &&
          isReisegiganten(metaStore.metaAffiliateId) &&
          !itineraryStore.selectedItinerary
        ) {
          trackReisegigantenNoAvailability();
        }
      },
    })
  );

  let previousUserCriteria: ItinerarySearchUserCriteria =
    criteriaStore.itineraryCriteria;

  model.itinerarySearchResults.subscribe((result) => {
    clearItineraryData();

    previousUserCriteria = result.criteria.userCriteria;

    result.itineraries.pipe(take(1)).subscribe((itinerary) => {
      if (!preferredTimeStore.isDateInExpandedMonth(itinerary.date)) {
        preferredTimeStore.expandMonth(itinerary.date);
      }
    });

    result.itineraries.subscribe({
      next: (itinerary) => {
        insertCurrentItinerary(itinerary);

        const allCarrierCodes = uniq(
          [
            ...itinerary.flightOnlyItinerary,
            ...itinerary.ferryOnlyItinerary,
          ].reduce((carriers: string[], transportIngredientResponse) => {
            if (
              isAvailableTransportIngredientResponse(
                transportIngredientResponse
              )
            ) {
              carriers.push(...transportIngredientResponse.allCarriers);
            }

            return carriers;
          }, [])
        ).filter((carrierCode) => !searchedCarrierNames.has(carrierCode));

        allCarrierCodes.forEach((carrierCode) =>
          searchedCarrierNames.add(carrierCode)
        );

        model.searchCarrierNames(allCarrierCodes).subscribe((carrierNames) => {
          tripStore.insertCarrierNames(carrierNames);
        });
      },
      complete: () => {
        itineraryStore.itinerarySearchCounter--;
      },
    });

    result.mostPopularItinerarySuggestions.subscribe(
      (itinerarySuggestionPrices) =>
        itineraryStore.insertItinerarySuggestionPrice(
          SuggestionType.MostPopular,
          itinerarySuggestionPrices
        )
    );
    result.cheapestItinerarySuggestions.subscribe((itinerarySuggestionPrices) =>
      itineraryStore.insertItinerarySuggestionPrice(
        SuggestionType.Cheapest,
        itinerarySuggestionPrices
      )
    );

    forkJoin({
      mostPopularItinerarySuggestionPrice:
        result.mostPopularItinerarySuggestionPrice,
      cheapestItinerarySuggestionPrice: result.cheapestItinerarySuggestionPrice,
    }).subscribe(
      ({
        mostPopularItinerarySuggestionPrice,
        cheapestItinerarySuggestionPrice,
      }) => {
        itineraryStore.mostPopularItinerarySuggestionPrice =
          mostPopularItinerarySuggestionPrice;
        itineraryStore.cheapestItinerarySuggestionPrice =
          cheapestItinerarySuggestionPrice;
        itineraryStore.globalSuggestionItinerariesCounter--;
      }
    );

    combineLatest([result.itineraries, result.cheapestItinerary]).subscribe(
      ([itineraries, cheapestItinerary]: [
        itineraries: ItineraryWithPrice,
        cheapestItinerary: ItineraryWithPrice
      ]) =>
        setSuggestedNumberOfNigthsToRecipeFromItinerary(
          itineraries || cheapestItinerary
        )
    );

    function clearItineraryData(): void {
      if (
        shouldClearSingleItinerary(
          result.criteria,
          previousUserCriteria,
          itineraryStore.userSelectedItinerary
        )
      ) {
        itineraryStore.clearSingleItinerary(
          itineraryStore.userSelectedItinerary.date
        );
      } else {
        tripStore.clearOverviewStaticInfo();
        itineraryStore.clearItineraries();
      }
    }

    function insertCurrentItinerary(itinerary: ItineraryWithPrice): void {
      if (
        shouldSearchCurrentItinerary(
          itinerary,
          itineraryStore.userSelectedItinerary
        )
      ) {
        itineraryStore.clearSingleItinerary(itinerary.date);

        searchItinerariesFromCriteria({
          userCriteria: {
            ...criteriaStore.itineraryCriteria,
            startSearchDate: itinerary.date,
            endSearchDate: itinerary.date,
            selectedDate: itinerary.date,
          },
          businessCriteria: criteriaStore.businessCriteria,
          shouldCancelPreviousOngoingRequest: false,
        });
      } else {
        itineraryStore.insertItinerary(itinerary);
      }
    }

    function setSuggestedNumberOfNigthsToRecipeFromItinerary(
      itinerary: ItineraryWithPrice
    ): void {
      [
        ...itinerary.hotelOnlyItinerary,
        ...itinerary.ownStayOnlyItinerary,
      ].forEach((stayIngredientResponse) => {
        if (stayIngredientResponse.isNumberOfNightsSuggested) {
          const recipeStayIngredient = recipeStore.getStayIngredient(
            stayIngredientResponse.ingredientId
          );

          recipeStayIngredient.numberOfNights =
            stayIngredientResponse.numberOfNights;
        }
      });
    }

    result.cheapestItinerary
      .pipe(logged('cheapest itinerary'))
      .subscribe((itinerary) => (itineraryStore.cheapestItinerary = itinerary));

    result.staticInfo.subscribe({
      next: (overviewStaticInfoResult) => {
        tripStore.insertOverviewStaticInfo(overviewStaticInfoResult);

        recipeStore.hotelOnlyRecipe.forEach((hotelIngredient) => {
          updateDestinationAvailabilitySearchRange(hotelIngredient);
        });
      },
      complete: () => itineraryStore.staticItinerarySearchCounter--,
    });
  });

  function updateDestinationAvailabilitySearchRange(
    hotelIngredient: HotelIngredient
  ): void {
    const ingredientStaticInfo = Object.values(
      tripStore.destinationStaticInfo
    ).filter(
      (destination) => destination.ingredientId === hotelIngredient.ingredientId
    );
    const uniqueResult =
      ingredientStaticInfo.length === 1 ? ingredientStaticInfo[0] : undefined;

    if (uniqueResult) {
      const staticInfo = tripStore.getOverviewStaticInfo(
        uniqueResult.hotelCode,
        uniqueResult.ingredientId
      );

      const polygonId = staticInfo?.mainPolygon?.id;
      const polygonFullName = staticInfo?.mainPolygon?.fullName;
      if (
        polygonId &&
        hotelIngredient.searchTerm.type === SearchTermType.PolygonId &&
        hotelIngredient.searchTerm.polygonId !== polygonId
      ) {
        hotelIngredient.searchTerm.polygonId = polygonId;
      }
      if (
        polygonFullName &&
        hotelIngredient.searchTerm.type === SearchTermType.FreeText &&
        hotelIngredient.searchTerm.freeText !== polygonFullName
      ) {
        hotelIngredient.searchTerm.freeText = polygonFullName;
      }
      hotelIngredient.nearbyPolygon = {
        name: polygonFullName,
        id: staticInfo?.mainPolygon?.id,
      };
      hotelIngredient.commonPolygon = {
        name: polygonFullName,
        id: staticInfo?.mainPolygon?.id,
      };
    }
  }

  const throttleTimeMiliseconds = 500;
  asObservable(selectedItinerary)
    .pipe(
      throttleTime(throttleTimeMiliseconds, undefined, {
        leading: true,
        trailing: true,
      }),
      distinctUntilChanged(
        (previousItinerary, currentItinerary) =>
          previousItinerary?.key === currentItinerary?.key
      )
    )
    .subscribe((itinerary) => {
      if (!itinerary) {
        return;
      }

      const intineraryFlightCarrierCodes = uniq(
        itinerary.flightOnlyItinerary.reduce(
          (carrierCodes: string[], current: FlightIngredientResponse) => {
            let leg: TransportLeg | undefined;

            if (isAvailableFlightIngredientResponse(current)) {
              leg = current.legs[current.legIndex];
            } else if (current.alternativeSuggestion?.legs) {
              leg =
                current.alternativeSuggestion.legs[
                  current.alternativeSuggestion.legIndex
                ];
            } else {
              return [...carrierCodes];
            }

            assertValue(
              leg,
              'itineraryViewModel.ts > asObservable(selectedItinerary) > leg'
            );

            const segmentCarrier = leg.segments.flatMap((segment) => [
              segment.carrier,
              segment.operatingCarrier,
            ]);

            return [...carrierCodes, ...segmentCarrier];
          },
          []
        )
      );

      const intineraryFerryCarrierCodes = uniq(
        itinerary.ferryOnlyItinerary.reduce(
          (carriers: string[], current: FerryIngredientResponse) => {
            if (isAvailableTransportIngredientResponse(current)) {
              carriers.push(...current.carriers);
            }

            return carriers;
          },
          []
        )
      );

      model
        .searchCarrierStaticInfo([
          ...intineraryFlightCarrierCodes,
          ...intineraryFerryCarrierCodes,
        ])
        .subscribe(tripStore.setCarrierStaticInfo);

      const itineraryAirportIatas = uniq(
        itinerary.flightOnlyItinerary.flatMap((flightIngredientResponse) => {
          if (
            isAvailableTransportIngredientResponse(flightIngredientResponse) &&
            flightIngredientResponse.legs
          ) {
            const leg =
              flightIngredientResponse.legs[flightIngredientResponse.legIndex];
            assertValue(
              leg,
              'itineraryViewModel.ts > itineraryAirportIatas > leg'
            );

            return flatIatas(leg.segments);
          } else if (flightIngredientResponse.alternativeSuggestion) {
            const leg =
              flightIngredientResponse.alternativeSuggestion.legs[
                flightIngredientResponse.alternativeSuggestion.legIndex
              ];
            assertValue(
              leg,
              'itineraryViewModel.ts > itineraryAirportIatas > alternative leg'
            );

            return flatIatas(leg.segments);
          }

          const ingredient = recipeStore.getTransportIngredient(
            flightIngredientResponse.ingredientId
          );

          assertValue(
            ingredient.fromPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itineraryAirportIatas > fromPort'
          );
          assertValue(
            ingredient.toPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itineraryAirportIatas > toPort'
          );

          return [ingredient.fromPort, ingredient.toPort];
        })
      );

      const newAirportIatas = itineraryAirportIatas.filter(
        (iata) => !tripStore.getPortStaticInfo(iata, IngredientType.Flight)
      );
      model
        .searchAirportStaticInfo(newAirportIatas)
        .subscribe((ports: Record<string, PortStaticInfo>) => {
          tripStore.insertPortStaticInfo(ports, IngredientType.Flight);
          tripStore.setFlightStaticInfoToRecipe();
        });

      // Ferries
      const itinerarySeaportIatas = itinerary.ferryOnlyItinerary.flatMap(
        (ferryIngredientResponse) => {
          if (isAvailableTransportIngredientResponse(ferryIngredientResponse)) {
            return [
              ferryIngredientResponse.arrivalIata,
              ferryIngredientResponse.departureIata,
            ];
          }

          const ingredient = recipeStore.getTransportIngredient(
            ferryIngredientResponse.ingredientId
          );

          assertValue(
            ingredient.fromPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itinerarySeaportIatas > fromPort'
          );
          assertValue(
            ingredient.toPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itinerarySeaportIatas > toPort'
          );

          return [ingredient.fromPort, ingredient.toPort];
        }
      );
      const newSeaportIatas = itinerarySeaportIatas.filter(
        (iata) => !tripStore.getPortStaticInfo(iata, IngredientType.Ferry)
      );
      tripStore.updateSearchingFerriesStaticInfo(true);
      model.searchSeaportStaticInfo(newSeaportIatas).subscribe({
        next: (ports: Record<string, PortStaticInfo>) => {
          tripStore.insertPortStaticInfo(ports, IngredientType.Ferry);
          tripStore.setFerryStaticInfoToRecipe();
        },
        complete: () => {
          tripStore.updateSearchingFerriesStaticInfo(false);
        },
        error: () => tripStore.updateSearchingFerriesStaticInfo(false),
      });
    });

  searchItinerariesFromCriteria({
    userCriteria: criteriaStore.itineraryCriteria,
    businessCriteria: criteriaStore.businessCriteria,
  });
}
